import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, Store, RefreshCw, Unlock, Lock, Undo2, CheckCircle,
  AlertTriangle, X, Banknote, CreditCard, Building, DollarSign, Gem,
  TrendingUp, TrendingDown, FileWarning, User, Printer, FileText, Package, Phone
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const MODES = [
  { key: 'especes',  label: 'ESPECES',   icon: Banknote,   color: 'from-green-500 to-emerald-600', text: 'text-green-700' },
  { key: 'carte',    label: 'CARTE',     icon: CreditCard, color: 'from-blue-500 to-blue-600',     text: 'text-blue-700' },
  { key: 'cheque',   label: 'CHEQUE',    icon: Building,   color: 'from-indigo-500 to-indigo-600', text: 'text-indigo-700' },
  { key: 'virement', label: 'VIREMENT',  icon: DollarSign, color: 'from-purple-500 to-purple-600', text: 'text-purple-700' },
  { key: 'or',       label: 'OR',        icon: Gem,        color: 'from-yellow-500 to-amber-600',  text: 'text-yellow-700' },
];

const fmt = (m) => parseFloat(m || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MAD';
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

const RapportJour = ({ onImprimer }) => {
  // Contexte
  const ctx = (() => { try { return JSON.parse(localStorage.getItem('workspaceContext') || '{}'); } catch { return {}; } })();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role || ''; } catch { return ''; } })();
  const canReouvrir = ['admin', 'manager'].includes(userRole);

  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueId, setBoutiqueId] = useState(ctx.boutique_id || ctx.boutiqueId || '');
  const [dateJour, setDateJour] = useState(today());

  const [rapport, setRapport] = useState(null);
  const [session, setSession] = useState(null); // cloture row matching date_journee
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals
  const [showOuvrir, setShowOuvrir] = useState(false);
  const [showReouvrir, setShowReouvrir] = useState(false);
  const [showRefermer, setShowRefermer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [motifReouvrir, setMotifReouvrir] = useState('');
  const [commentaireRefermer, setCommentaireRefermer] = useState('');

  // Bug 2 : erreurs locales aux modals
  const [modalErrorOuvrir, setModalErrorOuvrir] = useState('');
  const [modalErrorReouvrir, setModalErrorReouvrir] = useState('');
  const [modalErrorRefermer, setModalErrorRefermer] = useState('');

  // Modal Annuler la caisse (patch 36)
  const [showAnnulerModal, setShowAnnulerModal] = useState(false);
  const [annulerStep, setAnnulerStep] = useState(1);
  const [annulerPdfDownloaded, setAnnulerPdfDownloaded] = useState(false);
  const [annulerMotif, setAnnulerMotif] = useState('');
  const [annulerConfirmText, setAnnulerConfirmText] = useState('');
  const [annulerSubmitting, setAnnulerSubmitting] = useState(false);
  const [annulerError, setAnnulerError] = useState('');

  // Facture detail modal (SUJET A partie 1)
  const [showFactureModal, setShowFactureModal] = useState(false);
  const [factureDetail, setFactureDetail] = useState(null);
  const [loadingFacture, setLoadingFacture] = useState(false);
  const [errorFacture, setErrorFacture] = useState('');

  const openFactureModal = async (venteId) => {
    if (!venteId) return;
    setShowFactureModal(true);
    setLoadingFacture(true);
    setErrorFacture('');
    setFactureDetail(null);
    try {
      const r = await api.get(`${API_URL}/ventes/${venteId}/details`);
      setFactureDetail(r.data);
    } catch (e) {
      setErrorFacture(e.response?.data?.error || 'Erreur chargement facture');
    } finally {
      setLoadingFacture(false);
    }
  };

  const closeFactureModal = () => {
    setShowFactureModal(false);
    setFactureDetail(null);
    setErrorFacture('');
  };

  const MODE_LABELS = {
    especes: 'Especes', carte: 'Carte bancaire', cheque: 'Cheque',
    virement: 'Virement', or: 'Or (deduction)',
    credit: 'Credit (dette)', avoir: 'Avoir client', compte: 'Compte client'
  };
  const fmtDateTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const flashSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  // Bug 3 : helper flashError symetrique (auto-dismiss 5s)
  const flashError = (msg) => { setError(msg); setTimeout(() => setError(''), 5000); };

  const loadBoutiques = useCallback(async () => {
    try {
      const r = await api.get(`${API_URL}/boutiques`);
      const list = Array.isArray(r.data) ? r.data : [];
      setBoutiques(list.filter(b => b.type !== 'central'));
      if (!boutiqueId && list.length > 0) setBoutiqueId(list[0].id);
    } catch (e) { console.error(e); }
  }, [boutiqueId]);

  const loadRapport = useCallback(async () => {
    if (!boutiqueId || !dateJour) return;
    setLoading(true); setError('');
    try {
      const r = await api.get(`${API_URL}/clotures-caisse/rapport-jour?boutique_id=${boutiqueId}&date=${dateJour}`);
      setRapport(r.data);
    } catch (e) {
      flashError(e.response?.data?.error || 'Erreur chargement rapport');
      setRapport(null);
    } finally { setLoading(false); }
  }, [boutiqueId, dateJour]);

  const loadSession = useCallback(async () => {
    if (!boutiqueId || !dateJour) { setSession(null); return; }
    try {
      const r = await api.get(`${API_URL}/clotures-caisse?boutique_id=${boutiqueId}`);
      const list = Array.isArray(r.data) ? r.data : [];
      const match = list.find(c => c.date_journee && String(c.date_journee).slice(0, 10) === dateJour);
      setSession(match || null);
    } catch (e) { setSession(null); }
  }, [boutiqueId, dateJour]);

  useEffect(() => { loadBoutiques(); }, [loadBoutiques]);
  useEffect(() => { loadRapport(); loadSession(); }, [loadRapport, loadSession]);

  // Actions
  const ouvrirJour = async () => {
    setSubmitting(true); setModalErrorOuvrir('');
    try {
      await api.post(`${API_URL}/clotures-caisse/ouvrir-jour`, { boutique_id: parseInt(boutiqueId), date_jour: dateJour });
      flashSuccess('Session ouverte pour le ' + dateJour);
      setShowOuvrir(false);
      await Promise.all([loadRapport(), loadSession()]);
    } catch (e) { setModalErrorOuvrir(e.response?.data?.error || 'Erreur ouverture'); }
    finally { setSubmitting(false); }
  };

  const reouvrir = async () => {
    if (motifReouvrir.trim().length < 10) { setModalErrorReouvrir('Motif obligatoire (min 10 caracteres)'); return; }
    setSubmitting(true); setModalErrorReouvrir('');
    try {
      await api.post(`${API_URL}/clotures-caisse/reouvrir`, { cloture_id: session.id, motif: motifReouvrir.trim() });
      flashSuccess('Cloture reouverte');
      setShowReouvrir(false); setMotifReouvrir('');
      await Promise.all([loadRapport(), loadSession()]);
    } catch (e) { setModalErrorReouvrir(e.response?.data?.error || 'Erreur reouverture'); }
    finally { setSubmitting(false); }
  };

  // Annulation caisse (patch 36)
  const resetAnnulerStates = () => {
    setAnnulerStep(1);
    setAnnulerPdfDownloaded(false);
    setAnnulerMotif('');
    setAnnulerConfirmText('');
    setAnnulerError('');
    setAnnulerSubmitting(false);
  };
  const openAnnulerModal = () => {
    resetAnnulerStates();
    setShowAnnulerModal(true);
  };
  const closeAnnulerModal = () => {
    if (annulerSubmitting) return;
    setShowAnnulerModal(false);
    resetAnnulerStates();
  };
  const downloadPDFEtAutoCheck = () => {
    if (onImprimer) onImprimer(boutiqueId, dateJour);
    setAnnulerPdfDownloaded(true);
  };
  const submitAnnulation = async () => {
    if (!session?.id) { setAnnulerError('Session introuvable'); return; }
    setAnnulerSubmitting(true); setAnnulerError('');
    try {
      const r = await api.post(`${API_URL}/clotures-caisse/${session.id}/annuler-journee`, {
        motif: annulerMotif.trim(),
        confirmation_text: annulerConfirmText,
      });
      const archiveId = r.data?.archive_id || r.data?.id || '';
      flashSuccess(`Session annulee. Archive #${archiveId}. Caisse remise a zero.`);
      setShowAnnulerModal(false);
      resetAnnulerStates();
      await Promise.all([loadRapport(), loadSession()]);
    } catch (e) {
      setAnnulerError(e.response?.data?.error || 'Erreur annulation');
    } finally {
      setAnnulerSubmitting(false);
    }
  };

  const refermer = async () => {
    setSubmitting(true); setModalErrorRefermer('');
    try {
      // Reutilise route /cloturer existante ; was_reouverte detecte cote backend
      await api.post(`${API_URL}/clotures-caisse/${session.id}/cloturer`, {
        total_especes_compte: parseFloat(session.total_especes_theorique || 0),
        total_carte_compte: parseFloat(session.total_carte_theorique || 0),
        total_cheque_compte: parseFloat(session.total_cheque_theorique || 0),
        total_virement_compte: parseFloat(session.total_virement_theorique || 0),
        commentaire_refermeture: commentaireRefermer,
      });
      flashSuccess('Session refermee');
      setShowRefermer(false); setCommentaireRefermer('');
      await Promise.all([loadRapport(), loadSession()]);
    } catch (e) { setModalErrorRefermer(e.response?.data?.error || 'Erreur refermeture'); }
    finally { setSubmitting(false); }
  };

  // Bloc data
  const bloc1 = rapport?.bloc1_encaissements_reels || { modes: {}, total: 0 };
  const bloc2 = rapport?.bloc2_dettes_creees || { liste: [], total: 0, nb_ventes_concernees: 0 };
  const bloc3 = rapport?.bloc3_encaissements_dettes_anciennes || { liste: [], total: 0, nb_encaissements: 0 };

  // BLOC 4 - mouvements detailles chronologiques (patch 39)
  const labelType = (t) => ({ vente: 'Vente', depot_client: 'Depot', encaissement_credit: 'Encaissement' }[t] || t);
  const labelMode = (m) => ({ especes: 'especes', carte: 'carte', cheque: 'cheque', virement: 'virement', autre: 'autre' }[m] || m || '-');
  const mouvementsAvecCumul = (() => {
    let cumul = 0;
    return (rapport?.bloc4_mouvements_detail || []).map(mvt => {
      cumul += parseFloat(mvt.montant || 0);
      return { ...mvt, cumul };
    });
  })();
  const totalCumul = mouvementsAvecCumul.length > 0 ? mouvementsAvecCumul[mouvementsAvecCumul.length - 1].cumul : 0;

  const statut = session?.statut || 'aucune';
  const canAnnuler = userRole === 'admin'
    && ['ouverte', 'reouverte'].includes(statut)
    && dateJour === today();
  const statutBadge = {
    aucune:   { txt: 'Aucune session', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
    ouverte:  { txt: 'Session ouverte', cls: 'bg-green-50 text-green-700 border-green-300' },
    cloturee: { txt: 'Session cloturee', cls: 'bg-red-50 text-red-700 border-red-300' },
    reouverte:{ txt: 'Session reouverte', cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  }[statut] || { txt: statut, cls: 'bg-gray-100 text-gray-700' };

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide"><Store className="h-3 w-3 inline mr-1" />Boutique</label>
            <select value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
              <option value="">-- Choisir une boutique --</option>
              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide"><Calendar className="h-3 w-3 inline mr-1" />Date journee</label>
            <input type="date" value={dateJour} onChange={e => setDateJour(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
          </div>
          <div className="flex gap-2">
            <span className={`px-3 py-2 rounded-lg border font-medium text-sm ${statutBadge.cls}`}>{statutBadge.txt}</span>
            <button onClick={() => { loadRapport(); loadSession(); }} title="Rafraichir"
              className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-gray-700"><RefreshCw className="h-4 w-4" /></button>
            {onImprimer && (
              <button onClick={() => onImprimer(boutiqueId, dateJour)} title="Imprimer"
                disabled={!boutiqueId || !dateJour}
                className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 px-3 py-2 rounded-lg text-gray-700"><Printer className="h-4 w-4" /></button>
            )}
          </div>
        </div>

        {/* Action buttons contextuels */}
        <div className="mt-4 flex flex-wrap gap-2">
          {statut === 'aucune' && (
            <button onClick={() => setShowOuvrir(true)} disabled={!boutiqueId}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg font-medium shadow">
              <Unlock className="h-4 w-4" /> Ouvrir la session
            </button>
          )}
          {statut === 'cloturee' && (
            <button onClick={() => canReouvrir && setShowReouvrir(true)}
              disabled={!canReouvrir}
              title={canReouvrir ? 'Reouvrir cette cloture' : 'Reserve aux admin et manager'}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium shadow ${canReouvrir ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              <Undo2 className="h-4 w-4" /> Reouvrir la cloture
            </button>
          )}
          {statut === 'reouverte' && (
            <button onClick={() => setShowRefermer(true)}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow">
              <Lock className="h-4 w-4" /> Refermer la session
            </button>
          )}
        </div>

        {/* Actions dangereuses (patch 36) */}
        {canAnnuler && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs text-red-600 font-semibold mb-2 uppercase tracking-wide flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Actions dangereuses
            </div>
            <button onClick={openAnnulerModal}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold shadow">
              <AlertTriangle className="h-4 w-4" /> Annuler la caisse
            </button>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><div className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /><span>{error}</span></div><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2"><CheckCircle className="h-5 w-5" /><span>{success}</span></div>}

      {loading && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div></div>}

      {!loading && rapport && (
        <>
          {/* BLOC 1 - Encaissements reels */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span>BLOC 1 — ENCAISSEMENTS REELS DU JOUR</span>
              <span className="ml-auto text-sm text-gray-500">Total : <strong className="text-gray-900">{fmt(bloc1.total)}</strong></span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {MODES.map(m => {
                const data = bloc1.modes?.[m.key] || { nb: 0, total: 0 };
                const Icon = m.icon;
                return (
                  <div key={m.key} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <div className={`bg-gradient-to-r ${m.color} px-3 py-2 flex items-center gap-2`}>
                      <Icon className="h-4 w-4 text-white" />
                      <span className="text-white text-xs font-bold tracking-wide">{m.label}</span>
                    </div>
                    <div className="px-3 py-3">
                      <p className={`text-xl font-black ${m.text}`}>{fmt(data.total)}</p>
                      <p className="text-xs text-gray-500 mt-1">{data.nb} mouvement{data.nb > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {bloc1.breakdown && (
              <div className="mt-4 bg-gray-50 border rounded-lg p-3">
                <div className="text-xs font-semibold text-gray-600 uppercase mb-2 tracking-wide">
                  Detail par origine
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Ventes du jour ({bloc1.breakdown?.vente?.nb || 0})</span>
                    <span className="font-semibold text-gray-900">{fmt(bloc1.breakdown?.vente?.total || 0)} MAD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Depots clients ({bloc1.breakdown?.depot_client?.nb || 0})</span>
                    <span className="font-semibold text-gray-900">{fmt(bloc1.breakdown?.depot_client?.total || 0)} MAD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Encaissements dettes anciennes ({bloc1.breakdown?.encaissement_credit?.nb || 0})</span>
                    <span className="font-semibold text-gray-900">{fmt(bloc1.breakdown?.encaissement_credit?.total || 0)} MAD</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* BLOC 2 - Dettes creees */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-orange-600" />
              <span>BLOC 2 — DETTES CREEES DU JOUR</span>
              <span className="ml-auto text-sm text-gray-500">{bloc2.nb_ventes_concernees} vente(s) — Total : <strong className="text-orange-700">{fmt(bloc2.total)}</strong></span>
            </h2>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {bloc2.liste.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Aucune dette creee ce jour</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-orange-50 text-orange-800 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Facture</th>
                      <th className="px-3 py-2 text-left">Article</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-right">Total vente</th>
                      <th className="px-3 py-2 text-right">Dette creee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bloc2.liste.map(l => (
                      <tr key={l.vente_id} className="hover:bg-orange-50/30">
                        <td
                          className="px-3 py-2 font-mono text-xs cursor-pointer text-blue-700 hover:bg-blue-50 hover:underline transition"
                          onClick={() => openFactureModal(l.vente_id)}
                          title="Voir detail facture"
                        >
                          {l.num_facture || '-'}
                        </td>
                        <td className="px-3 py-2">{l.id_produit || '-'}</td>
                        <td className="px-3 py-2"><User className="h-3 w-3 inline mr-1 text-gray-400" />{l.client_nom || '-'}</td>
                        <td className="px-3 py-2 text-right">{fmt(l.montant_vente_total)}</td>
                        <td className="px-3 py-2 text-right font-bold text-orange-700">{fmt(l.montant_dette_creee)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* BLOC 3 - Encaissements dettes anciennes */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-blue-600" />
              <span>BLOC 3 — ENCAISSEMENTS DETTES ANCIENNES</span>
              <span className="ml-auto text-sm text-gray-500">{bloc3.nb_encaissements} reglement(s) — Total : <strong className="text-blue-700">{fmt(bloc3.total)}</strong></span>
            </h2>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {bloc3.liste.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Aucun reglement de dette ancienne ce jour</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-blue-50 text-blue-800 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Mode</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Montant</th>
                      <th className="px-3 py-2 text-right">Solde actuel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bloc3.liste.map(l => (
                      <tr key={l.mc_id} className="hover:bg-blue-50/30">
                        <td className="px-3 py-2"><User className="h-3 w-3 inline mr-1 text-gray-400" />{l.client_nom || '-'}</td>
                        <td className="px-3 py-2 uppercase text-xs font-bold">{l.mode_paiement || '-'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{l.description || '-'}</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-700">{fmt(l.montant)}</td>
                        <td className={`px-3 py-2 text-right text-xs ${l.client_solde_actuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(l.client_solde_actuel)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* BLOC 4 - Mouvements detailles chronologiques (patch 39) */}
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span aria-hidden="true">&#128340;</span>
              <span>BLOC 4 — MOUVEMENTS DETAILLES ({mouvementsAvecCumul.length})</span>
              <span className="ml-auto text-sm text-gray-500">Total : <strong className="text-gray-900">{fmt(totalCumul)}</strong></span>
            </h2>
            <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
              {mouvementsAvecCumul.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Aucun mouvement de caisse ce jour</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide border-b">
                    <tr>
                      <th className="px-3 py-2 text-left">Heure</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Detail / Bijou</th>
                      <th className="px-3 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">Mode</th>
                      <th className="px-3 py-2 text-left">Caisse</th>
                      <th className="px-3 py-2 text-right">Montant</th>
                      <th className="px-3 py-2 text-right">Cumul</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {mouvementsAvecCumul.map(mvt => {
                      const typeColor = mvt.type === 'vente'
                        ? 'bg-green-100 text-green-700'
                        : mvt.type === 'depot_client'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700';
                      const isClickable = mvt.type === 'vente' && mvt.vente_id;
                      return (
                        <tr
                          key={mvt.id}
                          className={`hover:bg-blue-50/40 ${isClickable ? 'cursor-pointer' : ''}`}
                          onClick={isClickable ? () => openFactureModal(mvt.vente_id) : undefined}
                          title={isClickable ? 'Cliquer pour voir la facture' : undefined}
                        >
                          <td className="px-3 py-2 font-mono text-xs text-gray-600">{mvt.heure || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${typeColor}`}>{labelType(mvt.type)}</span>
                          </td>
                          <td className="px-3 py-2">{mvt.client_nom || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{mvt.detail || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600">{mvt.reference || '-'}</td>
                          <td className="px-3 py-2 text-xs">{labelMode(mvt.mode_paiement)}</td>
                          <td className="px-3 py-2 text-xs">{mvt.type_caisse || '-'}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmt(mvt.montant)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmt(mvt.cumul)}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan="7" className="px-3 py-2 text-right">TOTAL :</td>
                      <td className="px-3 py-2 text-right">{fmt(totalCumul)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal Ouvrir jour */}
      {showOuvrir && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Unlock className="h-5 w-5 text-green-600" /> Ouvrir la session</h3>
              <button onClick={() => { setShowOuvrir(false); setModalErrorOuvrir(''); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Ouvrir une session journaliere pour <strong>{boutiques.find(b => b.id === parseInt(boutiqueId))?.nom || '-'}</strong> au <strong>{dateJour}</strong>.</p>
            {modalErrorOuvrir && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-2 mb-3 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /><span>{modalErrorOuvrir}</span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowOuvrir(false); setModalErrorOuvrir(''); }} disabled={submitting} className="px-4 py-2 border rounded-lg">Annuler</button>
              <button onClick={ouvrirJour} disabled={submitting} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium disabled:bg-gray-300">
                {submitting ? 'Ouverture...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reouvrir */}
      {showReouvrir && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Undo2 className="h-5 w-5 text-orange-600" /> Reouvrir la cloture</h3>
              <button onClick={() => { setShowReouvrir(false); setModalErrorReouvrir(''); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">Action auditee : snapshot conserve + trace utilisateur.</p>
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase">Motif (min 10 caracteres) *</label>
            <textarea value={motifReouvrir} onChange={e => setMotifReouvrir(e.target.value)} rows={3}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500"
              placeholder="Ex: Correction totaux especes apres recomptage" />
            <p className="text-xs text-gray-400 mt-1">{motifReouvrir.trim().length} / 10</p>
            {modalErrorReouvrir && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-2 mt-3 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /><span>{modalErrorReouvrir}</span>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowReouvrir(false); setModalErrorReouvrir(''); }} disabled={submitting} className="px-4 py-2 border rounded-lg">Annuler</button>
              <button onClick={reouvrir} disabled={submitting || motifReouvrir.trim().length < 10}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium disabled:bg-gray-300">
                {submitting ? 'Reouverture...' : 'Reouvrir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Refermer */}
      {showRefermer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2"><Lock className="h-5 w-5 text-red-600" /> Refermer la session</h3>
              <button onClick={() => { setShowRefermer(false); setModalErrorRefermer(''); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">La refermeture sera tracee dans <code className="text-xs bg-gray-100 px-1">cloture_reouvertures</code>.</p>
            <label className="block text-xs font-bold text-gray-600 mb-1 uppercase">Commentaire refermeture (optionnel)</label>
            <textarea value={commentaireRefermer} onChange={e => setCommentaireRefermer(e.target.value)} rows={3}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500"
              placeholder="Ex: Correction validee" />
            {modalErrorRefermer && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-2 mt-3 text-sm">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /><span>{modalErrorRefermer}</span>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setShowRefermer(false); setModalErrorRefermer(''); }} disabled={submitting} className="px-4 py-2 border rounded-lg">Annuler</button>
              <button onClick={refermer} disabled={submitting}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium disabled:bg-gray-300">
                {submitting ? 'Refermeture...' : 'Refermer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detail Facture (SUJET A partie 1) */}
      {showFactureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                <h3 className="text-lg font-bold">
                  {factureDetail?.vente?.num_facture
                    ? `Detail facture ${factureDetail.vente.num_facture}`
                    : 'Detail facture'}
                </h3>
                {factureDetail?.vente?.statut === 'annulee' && (
                  <span className="ml-2 px-2 py-0.5 rounded bg-red-500 text-white text-xs font-bold">ANNULEE</span>
                )}
              </div>
              <button onClick={closeFactureModal} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>

            <div className="overflow-y-auto p-6 space-y-4">
              {loadingFacture && (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                </div>
              )}

              {errorFacture && !loadingFacture && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" /><span>{errorFacture}</span>
                </div>
              )}

              {!loadingFacture && !errorFacture && factureDetail && (() => {
                const v = factureDetail.vente || {};
                const t = factureDetail.totaux || {};
                const paiements = factureDetail.paiements || [];
                return (
                  <>
                    {/* Header info */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase font-bold mb-1"><Calendar className="h-3 w-3 inline mr-1" />Date vente</p>
                        <p className="text-sm font-medium">{fmtDateTime(v.date_vente)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase font-bold mb-1"><User className="h-3 w-3 inline mr-1" />Client</p>
                        <p className="text-sm font-medium">{v.client_nom || '-'}</p>
                        {v.client_telephone && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{v.client_telephone}</p>}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 uppercase font-bold mb-1"><Store className="h-3 w-3 inline mr-1" />Boutique</p>
                        <p className="text-sm font-medium">{v.boutique_nom || '-'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Vendeur : {v.vendeur_nom || 'inconnu'}</p>
                      </div>
                    </div>

                    {/* Article */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-indigo-50 px-4 py-2 border-b">
                        <p className="text-xs font-bold text-indigo-800 uppercase tracking-wide flex items-center gap-2"><Package className="h-4 w-4" />Article</p>
                      </div>
                      <div className="p-4 space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-gray-600">ID produit</span><span className="font-mono">{v.id_produit || '-'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Nom</span><span className="font-medium">{v.bijou_nom || '-'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">Reference</span><span className="font-mono text-xs">{v.reference_article || '-'}</span></div>
                      </div>
                    </div>

                    {/* Paiements */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-green-50 px-4 py-2 border-b">
                        <p className="text-xs font-bold text-green-800 uppercase tracking-wide flex items-center gap-2"><CreditCard className="h-4 w-4" />Paiements ({paiements.length})</p>
                      </div>
                      {paiements.length === 0 ? (
                        <p className="text-center text-gray-400 py-4 text-sm">Aucun paiement enregistre</p>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                            <tr>
                              <th className="px-4 py-2 text-left">Mode</th>
                              <th className="px-4 py-2 text-left">Reference</th>
                              <th className="px-4 py-2 text-right">Montant</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {paiements.map((p, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2">{MODE_LABELS[p.mode] || p.mode}</td>
                                <td className="px-4 py-2 text-gray-500 text-xs">{p.reference || '-'}</td>
                                <td className={`px-4 py-2 text-right font-medium ${p.montant < 0 ? 'text-orange-600' : 'text-gray-800'}`}>{fmt(p.montant)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Totaux */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-gray-700">Prix vente</span><span className="font-medium">{fmt(t.prix_vente)}</span></div>
                      {t.remise_montant > 0 && (
                        <div className="flex justify-between text-orange-600"><span>Remise</span><span>- {fmt(t.remise_montant)}</span></div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-purple-200 text-base font-bold"><span>Montant final</span><span>{fmt(t.montant_final)}</span></div>
                      <div className="flex justify-between pt-1"><span className="text-gray-700">Encaisse (cash + or)</span><span className="font-bold text-green-700">{fmt(t.total_encaisse_reel)}</span></div>
                      {t.mis_en_compte > 0 && (
                        <>
                          <div className="flex justify-between"><span className="text-gray-700">Mis en compte client</span><span className="font-bold text-orange-700">{fmt(t.mis_en_compte)}</span></div>
                          <div className="flex justify-between text-xs pl-4">
                            <span className="text-gray-500">└ solde client actuel</span>
                            <span className={v.client_solde_compte >= 0 ? 'text-green-600' : 'text-red-600'}>{fmt(v.client_solde_compte)}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {v.commentaire && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700">
                        <p className="text-xs font-bold text-yellow-700 mb-1 uppercase">Commentaire vente</p>
                        {v.commentaire}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end">
              <button onClick={closeFactureModal} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Annuler la caisse (patch 36) — workflow 3 etapes */}
      {showAnnulerModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            {/* Header commun rouge */}
            <div className="bg-red-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                <div>
                  <h3 className="text-lg font-bold">Annuler la caisse</h3>
                  <p className="text-xs text-red-100">Etape {annulerStep}/3 — {boutiques.find(b => String(b.id) === String(boutiqueId))?.nom || ''} — {dateJour}</p>
                </div>
              </div>
              <button onClick={closeAnnulerModal} disabled={annulerSubmitting}
                className="hover:bg-white/20 p-1 rounded disabled:opacity-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body scrollable */}
            <div className="overflow-y-auto p-6 space-y-4">
              {/* ETAPE 1 : PDF */}
              {annulerStep === 1 && (
                <>
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <p className="text-yellow-800 font-bold mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> IMPRIMEZ LE PDF AVANT DE CONTINUER
                    </p>
                    <p className="text-sm text-yellow-800">
                      Ce PDF contient toutes les ventes du jour (FAC + bijoux + clients + paiements detailles).
                      Il vous servira a refaire les ventes apres l'annulation.
                      <strong> C'est votre seule trace.</strong>
                    </p>
                  </div>

                  <button onClick={downloadPDFEtAutoCheck}
                    className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-semibold">
                    <Printer className="h-4 w-4" /> Telecharger le PDF maintenant
                  </button>

                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input type="checkbox" checked={annulerPdfDownloaded}
                      onChange={e => setAnnulerPdfDownloaded(e.target.checked)}
                      className="h-4 w-4 text-red-600 rounded" />
                    <span>J'ai telecharge le PDF <span className="text-gray-400">(facultatif)</span></span>
                  </label>
                </>
              )}

              {/* ETAPE 2 : Motif + ANNULER */}
              {annulerStep === 2 && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Motif de l'annulation <span className="text-red-600">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">(min 20 caracteres)</span>
                    </label>
                    <textarea value={annulerMotif} onChange={e => setAnnulerMotif(e.target.value)}
                      rows={4}
                      placeholder="Ex: Erreur de saisie multiple, re-saisie necessaire apres verification des pieces manquantes..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm" />
                    <p className={`text-xs mt-1 ${annulerMotif.trim().length < 20 ? 'text-red-500' : 'text-green-600'}`}>
                      {annulerMotif.trim().length} / 20 caracteres {annulerMotif.trim().length >= 20 ? 'OK' : ''}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Pour confirmer, tapez exactement <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-700 font-bold">ANNULER</code> <span className="text-red-600">*</span>
                    </label>
                    <input type="text" value={annulerConfirmText}
                      onChange={e => setAnnulerConfirmText(e.target.value)}
                      placeholder="ANNULER"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono" />
                    {annulerConfirmText && annulerConfirmText !== 'ANNULER' && (
                      <p className="text-xs text-red-500 mt-1">Doit etre exactement "ANNULER" (majuscules)</p>
                    )}
                  </div>
                </>
              )}

              {/* ETAPE 3 : Recap + Confirmer */}
              {annulerStep === 3 && (
                <>
                  <p className="text-gray-700 font-semibold">Vous allez annuler :</p>
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Session caisse</span>
                      <span className="font-semibold">{dateJour} — {boutiques.find(b => String(b.id) === String(boutiqueId))?.nom || ''}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Total encaisse du jour</span>
                      <span className="font-bold text-red-700">{fmt(bloc1.total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Dettes creees ce jour</span>
                      <span className="font-semibold">{bloc2.nb_ventes_concernees || 0} vente(s) — {fmt(bloc2.total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Encaissements de dettes anciennes</span>
                      <span className="font-semibold">{bloc3.nb_encaissements || 0} mouvement(s) — {fmt(bloc3.total)}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Motif</p>
                    <p className="italic text-gray-700">"{annulerMotif.trim()}"</p>
                  </div>

                  <div className="bg-red-100 border-2 border-red-400 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-700 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-bold">Cette action est IRREVERSIBLE.</p>
                      <p className="text-xs">Les ventes, paiements, mouvements et dettes du jour seront marques comme annules. Une archive d'audit sera creee.</p>
                    </div>
                  </div>

                  {annulerError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{annulerError}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-between gap-2">
              {annulerStep === 1 && (
                <>
                  <button onClick={closeAnnulerModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
                    Annuler
                  </button>
                  <button onClick={() => setAnnulerStep(2)}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold">
                    Continuer &rarr;
                  </button>
                </>
              )}
              {annulerStep === 2 && (
                <>
                  <button onClick={() => setAnnulerStep(1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
                    &larr; Retour
                  </button>
                  <button onClick={() => setAnnulerStep(3)}
                    disabled={annulerMotif.trim().length < 20 || annulerConfirmText !== 'ANNULER'}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold">
                    Proceder &rarr;
                  </button>
                </>
              )}
              {annulerStep === 3 && (
                <>
                  <button onClick={() => setAnnulerStep(2)}
                    disabled={annulerSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                    &larr; Retour
                  </button>
                  <button onClick={submitAnnulation}
                    disabled={annulerSubmitting}
                    className="flex items-center gap-2 px-5 py-2 bg-red-700 hover:bg-red-800 disabled:bg-red-400 text-white rounded-lg font-bold shadow">
                    {annulerSubmitting ? (
                      <><RefreshCw className="h-4 w-4 animate-spin" /> Annulation en cours...</>
                    ) : (
                      <><AlertTriangle className="h-4 w-4" /> CONFIRMER L'ANNULATION</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RapportJour;
